<template>
  <main class="container">
    <div v-if="errorResponse">
      <WarningPanel
        :title="'There\'s a problem...'"
        :msg="errorResponse"
        :severe="true"
      />
    </div>
    <div v-else>
      <div class="summary-wrapper">
        <PackageSummary
          class="package-summary"
          :single-column="true"
          :show-optional-products="true"
          :allow-changing-optional="!isViaShareLink"
          :on-toggle-tv-licence="handleToggleTvLicence"
        >
          <template #summary-header>
            <div class="back-button-wrapper">
              <BackButton class="back-link" />
            </div>
            <div class="package-summary-header">
              <h1 data-testid="page-title">Your Package</h1>
              <span class="subheader" data-testid="package-summary-header">
                For {{ housemateCount }} housemates at
                <span class="address-or-postcode">{{ addressOrPostcode }}</span>
              </span>
            </div>
            <hr class="d-none-md-down" />
            <div id="price-discount-section" v-show="!isDiscountValidating">
              <section class="price-display">
                <div v-if="discountApplied" class="nondiscounted-price">
                  £{{ nonDiscountedPricePerPersonPerWeek }}
                </div>
                <div class="price">£{{ pricePerPersonPerWeek }}</div>
              </section>
              <div
                class="pppw"
                :class="{
                  'mb-30': !shouldShowVouchers,
                }"
              >
                per person per week. Based on a 12 month contract.
              </div>
              <monthly-price-info class="subheader padding-b-1" />
              <discount-activation
                v-if="shouldShowVouchers"
                v-model.trim="discountCode"
                @apply="applyDiscountCode"
                @clear="clearDiscountCode"
              />
            </div>
            <div
              id="price-discount-section"
              style="padding-top: 8rem; padding-bottom: 8rem;"
              v-if="isDiscountValidating"
            >
              <i
                class="fa-solid fa-spinner-third fa-spin fa-2x"
                data-testid="voucher-loading-animation"
              ></i>
            </div>
          </template>
        </PackageSummary>

        <button
          class="btn btn--big --main-cta"
          data-testid="summary-next-button"
          @click="join"
        >
          Next
        </button>

        <hr class="a-hr" />

        <h1 class="cta-why">Why UniHomes?</h1>
        <ul class="cta-list">
          <li>No more arguments chasing anyone for their share!</li>
          <li>
            We’ll set up everything and split payments into equal monthly
            payments
          </li>
          <li>
            We’re rated Excellent on&nbsp;<TrustpilotIcon class="trustpilot" />
          </li>
        </ul>
        <div class="actions">
          <button class="btn btn--big --save-cta" @click="showSaveShareModal">
            Save or Share Package<br />
            <small>Share this package with your housemates</small>
          </button>
        </div>

        <PackageSaveShareOverlay
          v-if="saveShareOverlayVisible"
          @close-panel="toggleSaveSharePanel"
        />
      </div>
    </div>
  </main>
</template>

<script>
import BackButton from "@/components/BackButton";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import PackageSummary from "@/components/PackageSummary";
import PackageSaveShareOverlay from "@/components/PackageSaveShareOverlay";
import WarningPanel from "@/components/WarningPanel";
import TrustpilotIcon from "@/assets/svg/trustpilot.svg";
import { findDiscountCode } from "@/utility/discount";
import DiscountActivation from "@/components/DiscountActivation.vue";
import {
  initialDiscountState,
  initialPackagePrice,
} from "@/store/initialState";
import MonthlyPriceInfo from "@/components/MonthlyPriceInfo.vue";

export default {
  name: "ShortSummary",
  data() {
    return {
      saveShareOverlayVisible: false,
      errorResponse: "",
    };
  },
  computed: {
    ...mapState({
      referral: (state) => state.referral,
      isViaShareLink: (state) => state.uiState.isViaShareLink,
      discount: (state) => state.discount,
    }),
    ...mapGetters({
      hasUsedDiscount: "hasUsedDiscount",
      housemateCount: "housemateCount",
      postcode: "postcode",
      hasAddress: "hasAddress",
      addressLine1: "addressLine1",
      canChangeBroadbandPackage: "canSkipBroadbandSelectionPage",
      shouldShowVouchers: "shouldShowVouchers",
    }),
    nonDiscountedPricePerPersonPerWeek() {
      return (this.$store.getters.nonDiscountedPricePerPerson / 100).toFixed(2);
    },
    pricePerPersonPerWeek() {
      return (this.$store.getters.pricePerPerson / 100).toFixed(2);
    },
    addressOrPostcode() {
      if (this.hasAddress) {
        return `${this.addressLine1}, ${this.postcode}`;
      } else {
        return this.postcode;
      }
    },
    discountCode: {
      get() {
        return this.$store.state.discount.code;
      },
      set(value) {
        this.$store.commit("SET_DISCOUNT_STATE", { code: value });
      },
    },
    discountApplied: {
      get() {
        return this.$store.state.discount.isValid;
      },
      set(value) {
        this.$store.commit("SET_DISCOUNT_STATE", { isValid: value });
      },
    },
    isDiscountValidating: {
      get() {
        return this.$store.state.discount.isValidating;
      },
      set(value) {
        this.$store.commit("SET_DISCOUNT_STATE", { isValidating: value });
      },
    },
  },
  methods: {
    ...mapActions([
      "getProducts",
      "getReferralMessage",
      "getAffiliateMessage",
      "updatePreSignupLeadProductData",
      "activateDiscount",
    ]),
    ...mapMutations({
      showLoadingPanel: "SHOW_LOADING_PANEL",
      hideLoadingPanel: "HIDE_LOADING_PANEL",
      showBackBtn: "SHOW_BACK_BTN",
      setProductOption: "SET_PRODUCT_OPTION",
      setPostcode: "SET_POSTCODE",
      setHousemateCount: "SET_HOUSEMATE_COUNT",
      setProducts: "SET_PRODUCTS",
      setAddresses: "SET_ADDRESSES",
      setPackagePrice: "SET_PACKAGE_PRICE",
      setDiscountState: "SET_DISCOUNT_STATE",
      setIsViaShareLink: "IS_VIA_SHARELINK",
      setLeadTenantAddress: "SET_LEAD_TENANT_ADDRESS",
    }),
    showSaveShareModal() {
      this.trackGoogleEvent("Click Share", "Get Price", "Share price");
      this.toggleSaveSharePanel();
    },
    toggleSaveSharePanel() {
      this.saveShareOverlayVisible = !this.saveShareOverlayVisible;
    },
    join() {
      this.trackGoogleEvent(
        "Click Create Account",
        "Create Account",
        "Create Account",
      );

      if (this.hasAddress) {
        this.$router.push("/your-details");
      } else {
        this.$router.push("/your-address");
      }
      if (this.hasAddress) {
        this.$router.push("/your-details");
      } else {
        this.$router.push("/your-address");
      }
    },
    editPackage() {
      this.$router.push(`/products/${this.$store.state.products[0].type}`);
    },
    applyDiscountCode() {
      this.$set(this, "isDiscountValidating", true);

      this.activateDiscount({
        voucherCode: this.$store.state.discount.code,
        selectedProducts: this.$store.getters.selectedProductPriceIds,
        housemateCount: this.$store.state.housemateCount,
        leadId: this.$store.state.leadId,
      })
        .then(({ data }) => {
          this.$set(this, "discountApplied", true);

          this.$store.commit("SET_PACKAGE_PRICE", {
            fullPriceAmount: data.priceBeforeDiscount,
            discountedPriceAmount: data.priceAfterDiscount,
          });

          this.$store.commit("SET_DISCOUNT_STATE", {
            description: data.message,
            errorMsg: "",
          });

          this.trackGoogleEvent(
            "Voucher applied successfully",
            "Voucher Apply",
            "Add Voucher",
          );
        })
        .catch((err) => {
          this.$store.commit("SET_PACKAGE_PRICE", initialPackagePrice());
          this.$store.commit("SET_DISCOUNT_STATE", {
            errorMsg:
              err.response.data.message ||
              "Invalid voucher code. Check and try again.",
          });
          this.$set(this, "discountApplied", false);

          this.trackGoogleEvent(
            `Voucher applied failed`,
            "Voucher Apply",
            "Add Voucher",
          );
        })
        .finally(() => {
          this.$set(this, "isDiscountValidating", false);
        });
    },
    handleToggleTvLicence() {
      // Keep nextTick in place to allow store to be updated prior to retrieving the updated selectedProductPriceIds, for discount code implementation
      this.$nextTick(() => {
        if (this.discountApplied) {
          this.applyDiscountCode();
        }
      });
    },
    clearDiscountCode() {
      this.$store.commit("SET_PACKAGE_PRICE", initialPackagePrice());
      this.$store.commit("SET_DISCOUNT_STATE", initialDiscountState());
    },
    loadShareLinkData() {
      const addressGuid = this.$route.query.address;

      this.getProducts({
        addressGuid,
        postcode: this.$route.query.postcode,
        housemateCount: this.$route.query.housemates,
        voucherCode: this.$route.query.voucherCode,
        hash: this.$route.query.hash,
      })
        .then(({ data }) => {
          // ensure errorResponse is reset
          this.errorResponse = "";

          if (data.address) {
            this.setLeadTenantAddress(data.address);
            this.setPostcode(data.address.postcode);
          }

          if (data.existingAccountGUID) {
            this.$router.push({
              name: "account-already-exists",
              params: { guid: data.existingAccountGUID },
            });
          } else {
            // set the store state
            if (this.$route.query.postcode) {
              this.setPostcode(this.$route.query.postcode);
            }
            this.setHousemateCount(this.$route.query.housemates);
            this.setProducts(data.products);

            if (data.priceBeforeDiscount) {
              this.nonDiscountedPricePerPersonPerWeekFormatted = (
                data.priceBeforeDiscount / 100
              ).toFixed(2);
            }

            if (data.priceAfterDiscount) {
              this.pricePerPersonPerWeekFormatted = (
                data.priceAfterDiscount / 100
              ).toFixed(2);
            }
            // Because the app has been entered mid-journey, we need to set a variable
            // that controls what the "back/editProduct" button directs to
            this.setIsViaShareLink(true);

            // Filter the query parameters ensuring the selected products are stored in state
            Object.keys(this.$route.query).forEach((key) => {
              // This mutation performs a find on the products array based on the
              // query key, so it's fine to pass the whole query parameter set
              this.setProductOption({
                type: key,
                code: this.$route.query[key],
              });
            });

            // If referral code, commit it to the store...
            if (this.$route.query.ref) {
              this.getReferralMessage(this.$route.query.ref);
            }

            // If affiliate code, commit it to the store...
            if (this.$route.query.aff) {
              this.getAffiliateMessage(this.$route.query.aff);
            }

            if (this.$route.query.voucherCode) {
              this.setDiscountState({
                inputRevealed: true,
                code: this.$route.query.voucherCode,
              });
              this.applyDiscountCode();
            }

            if (Object.hasOwn(data, "discount")) {
              this.$store.commit("SET_DISCOUNT_STATE", {
                isValid: data.discount.isValid,
                inputRevealed: data.discount.inputRevealed,
                offer: data.discount.offer,
                aff: data.discount.aff,
                code: data.discount.code,
              });

              if (Object.hasOwn(data, "leadId")) {
                this.$store.commit("SET_LEAD_ID", data.leadId);
              }
            }
          }
        })
        .catch((error) => {
          if (!error.response) {
            this.errorResponse =
              "Network error accessing the Split The Bills API";
          } else {
            this.errorResponse = error.response.data.messages[0];
          }
        })
        .finally(() => {
          this.hideLoadingPanel();
        });
    },
  },
  created() {
    this.showBackBtn();
    this.clearDiscountCode();

    // Check if this is a share link (has required query parameters)
    const hasAddressOrPostcode =
      this.$route.query.address || this.$route.query.postcode;
    const hasHousemates = this.$route.query.housemates;

    // Only run share link logic if we have the required parameters
    if (hasAddressOrPostcode && hasHousemates) {
      this.loadShareLinkData();
    } else {
      this.hideLoadingPanel();
    }
  },
  mounted() {
    findDiscountCode(this.$route.query, this.$store);
    this.updatePreSignupLeadProductData({
      offerCode: this.$store.state.discount.offer,
      affiliateCode: this.$store.state.discount.aff,
      referralCode: this.$route.query.ref,
    });
  },
  components: {
    BackButton,
    MonthlyPriceInfo,
    DiscountActivation,
    PackageSaveShareOverlay,
    PackageSummary,
    WarningPanel,
    TrustpilotIcon,
  },
};
</script>

<style lang="scss" scoped>
.container {
  max-width: 65rem;
}

.summary-wrapper {
  background-color: white;
  border-radius: $border-radius-large;
  padding: $spacing-small $spacing-large;
  margin-top: $spacing-large;

  @include media("<medium") {
    background-color: transparent;
    box-shadow: none;
    padding: 0;
    margin-top: 0;
  }

  .back-button-wrapper {
    text-align: left;

    .back-button.back-link {
      margin-bottom: 0;
      margin-top: 1rem;
    }
  }
}

.padding-b-1 {
  margin-bottom: 1rem;
}

.subheader {
  display: block;
  margin-top: $spacing-tiny;
  font-size: 16px;
  font-weight: $font-regular;
  color: $grey-dark;
}

.package-summary-header {
  //padding-top: 1rem;
  margin-bottom: 3rem;

  h1 {
    color: $brand-dark-blue;
    margin: 0;
  }

  .postcode {
    color: $brand-dark-blue;
    font-weight: $font-medium;
  }

  @include media("<medium") {
    font-size: 16px;
    margin-top: $spacing-medium;
  }
}

.package-summary {
  background: $white;
  text-align: center;
  padding-bottom: $spacing-small;

  .edit-pkg-button {
    margin-top: $spacing-xs;
    margin-bottom: 0;
    background: $brand-orange;

    @include media(">=medium") {
      display: none;
    }
  }
}

.price-display {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.3em;
  margin-bottom: 0.5em;
}

.nondiscounted-price {
  color: $brand-blue;
  text-decoration: line-through;
  font-size: $title-font-size-mob;
  font-weight: $font-regular;
}

.price {
  font-size: $title-font-size;
  font-weight: $font-medium;
  color: $brand-blue;
}

.package-price {
  text-align: center;
  margin-bottom: 2rem;
  line-height: 0.9;

  &-old {
    color: #959595;
    font-size: 16px;
    font-weight: $font-bold;
    text-decoration: line-through;
  }

  &-current {
    color: $brand-blue;
    font-size: 50px;
    font-weight: $font-bold;
  }

  &-pppw {
    font-size: 16px;
    margin-bottom: 0.3rem;
  }
}

.no-fees {
  color: $grey-dark;
  margin: 1.5rem 0;
  text-align: center;
  font-size: 16px;
}

.a-hr {
  margin-top: $spacing-medium;
}

.cta-why {
  color: $brand-dark-blue;
  margin-bottom: 2rem;
}

.cta-list {
  margin-bottom: $spacing-medium;
  color: $brand-dark-blue;

  list-style: none;
  padding-inline-start: 0;

  @include media("<medium") {
    width: 100%;
  }

  li {
    background: url("~@/assets/svg/check-blue.svg?external") left center
      no-repeat;
    padding-left: 30px;

    margin-bottom: $spacing-xs;
    font-size: $subtitle-font-size;

    &:last-child {
      margin-bottom: 0;
    }

    svg {
      vertical-align: sub;
    }
  }
}

.actions {
  text-align: center;

  .--save-cta {
    cursor: pointer;
    margin-top: 1rem;
    font-weight: $font-bold;
    background-color: $brand-dark-blue;

    &:hover {
      background-color: darken($brand-dark-blue, 5%);
    }
  }
}

.--main-cta {
  margin-bottom: $spacing-medium;

  @include media("<medium") {
    margin-bottom: $spacing-small;
  }
}

.summary-header {
  text-align: center;
  margin-bottom: $spacing-small;

  h2 {
    font-size: 18px;
    margin: 0;

    .address-or-postcode {
      display: block;
      color: $brand-orange;
      font-size: 21px;
      font-weight: $font-bold;
    }
  }
}

.summary-footer {
  padding: $spacing-small 0 0 0;
  text-align: center;
}

.back-link {
  margin: 2rem 0 1rem 0;
}

.trustpilot {
  display: inline;
  height: 20px;
}

.plain-link {
  cursor: pointer;
  color: $brand-dark-blue;
  text-decoration: underline;
  display: inline-block;
  margin: $spacing-xs 0 0 0;
  font-size: 16px;
  font-weight: $font-bold;
}

.mb-30 {
  margin-bottom: 30px;
}
</style>
